import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import OrderStatusModal from "./components/modal/status-orders/OrderStatusModal";
import { Plus, CheckCircle2, CookingPot, Truck, Minus, X } from "lucide-react";
import "./App.css";
import OrderStatusLine from "components/modal/status-orders/OrderStatusLine";
import ProductModal from "components/modal/product/ProductModal";
import { ProductsGrid } from "components/product/ProductsGrid";
import CartStepsModal from "components/modal/cart/CartStepsModal";
import { Header } from "components/header/Header";
import { BannerCarousel } from "components/carousel/BannerCarousel";
import {
  SearchAndFilters,
  CATALOG_TOOLBAR_ID,
} from "components/search/SearchAndFilters";
import { MobileCartBar } from "components/cart/MobileCartBar";
import { useCreateOrder } from "./hooks/useCreateOrder";
import { useCompany } from "./hooks/useCompany";
import { useCompanyBranding } from "./hooks/useCompanyBranding";
import { getWhatsAppNumber } from "./lib/company";
import { Footer } from "components/footer/Footer";
import { ScrollToTopButton } from "components/ui/ScrollToTopButton";
import { WhatsAppButton } from "components/ui/WhatsAppButton";
import { useAuth } from "./contexts/AuthContext";
import { useApi } from "./hooks/useApi";
import { AuthLoadingScreen } from "./components/ui/AuthLoadingScreen";
import { FEATURES } from "./config/features";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentReturnPage from "./pages/PaymentReturnPage";
import {
  OrderStatusProvider,
  useOrderStatus,
} from "./contexts/OrderStatusContext";
import { COMPANY_KEY } from "./config/company";
import { parseOrderStatus } from "./lib/orderStatus";

const FOOTER_DATA = {
  companyName: "",
  description: "",
  phone: "",
  deliveryInfo: "",
  businessHours: ["Segunda a Sexta: 9h às 22h", "Sábado e Domingo: 10h às 23h"],
};

/** Helper de formatação de moeda PT-BR */
const formatBRL = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
};

/** Máscara de telefone BR (com ou sem +55) */
const maskPhoneBR = (value) => {
  const d = String(value || "").replace(/\D/g, "");

  const formatNum = (num) => {
    if (num.length <= 4) return num;
    const head = num.slice(0, num.length - 4);
    const tail = num.slice(-4);
    return `${head}-${tail}`;
  };

  // Com DDI Brasil (+55)
  if (d.startsWith("55")) {
    const rest = d.slice(2, 13); // 2 (DDD) + até 9 dígitos
    const area = rest.slice(0, 2);
    const num = rest.slice(2);
    if (!area) return "+55 ";
    if (!num) return `+55 (${area}) `;
    return `+55 (${area}) ${formatNum(num)}`;
  }

  // Sem DDI
  const area = d.slice(0, 2);
  const num = d.slice(2, 11); // até 9 dígitos
  if (!area) return "";
  if (!num) return `(${area}) `;
  return `(${area}) ${formatNum(num)}`;
};

function App() {
  return <AppContent />;
}

function AppContent() {
  // TODOS os hooks devem estar aqui, SEM condicionais antes
  const { isAuthenticated, loading: authLoading, authError } = useAuth();
  const { sendToWhatsApp: sendOrderToWhatsApp, loading: whatsappLoading } =
    useCreateOrder();
  const api = useApi();
  const location = useLocation();
  // Dados da loja (header, rodapé, WhatsApp) — só depois do login guest
  const { company } = useCompany({ enabled: isAuthenticated });
  // Logo da empresa no favicon e nome no título da aba
  useCompanyBranding(company);

  // Hook do contexto de status de pedido
  const {
    isOrderStatusModalOpen,
    orderCode,
    openOrderStatusModal,
    closeOrderStatusModal,
  } = useOrderStatus();

  // Refs para controle de debounce
  const searchTimeoutRef = useRef(null);

  // Estados do componente
  const [products, setProducts] = useState([]);
  const [productsExtras, setProductsExtras] = useState([]);
  const [categories, setCategories] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Carrinho com persistência no localStorage
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("bonfim_cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Erro ao carregar carrinho do localStorage:", error);
      return [];
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCartValidateCode, setIsCartValidateCode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [deliveryType, setDeliveryType] = useState("pickup");
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  // Estados de pagamento
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [paymentMethodSelected, setPaymentMethodSelected] = useState("pix");
  const [pixPayNow, setPixPayNow] = useState(false);

  // Endereço para Delivery
  const [address, setAddress] = useState({
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    uf: "",
    estado: "",
  });

  // Estados adicionais
  const [uiBusy, setUiBusy] = useState(false);
  const [codeOrders, setCodeOrders] = useState("0");
  const [addressOrders, setAddressOrders] = useState("");
  const [pixLoading, setPixLoading] = useState(false);
  const [pixData, setPixData] = useState({
    id: "",
    qr_code: "",
    qr_code_base64: "",
    copy_paste: "",
  });
  const [webSocket, setWebSocket] = useState(0);
  const [open, setOpen] = useState(false);
  const [acompanhamento, setAcompanhamento] = useState(false);
  const [stockWarning, setStockWarning] = useState({
    show: false,
    title: "",
    message: "",
  });

  // Refs
  const productsSectionRef = useRef(null);

  // TODOS os useMemo, useCallback e useEffect DEVEM estar aqui, ANTES do early return
  const modalSizes = useMemo(() => {
    if (!selectedProduct) return [];
    return [
      {
        key: String(selectedProduct.weight ?? "0"),
        label:
          String(selectedProduct.weight ?? "0") +
          " " +
          String(selectedProduct.unid ?? "0"),
        key_product: String(selectedProduct.weight_product ?? "0"),
        label_product:
          String(selectedProduct.weight_product ?? "0") +
          " " +
          String(selectedProduct.unid_product ?? "0"),
        basePrice: Number(selectedProduct.price ?? 0),
        included: Number(selectedProduct.totalIngredients ?? 0),
        includedProduct: Number(selectedProduct.totalProductsSelects ?? 0),
      },
    ];
  }, [selectedProduct]);

  // Helper functions
  const onlyDigits = useCallback((value) => {
    return String(value || "").replace(/\D/g, "");
  }, []);

  const maskCEP = useCallback(
    (value) => {
      const digits = onlyDigits(value);
      if (digits.length <= 5) return digits;
      return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
    },
    [onlyDigits],
  );

  // Estados do UF
  const UF_NAMES = useMemo(
    () => ({
      AC: "Acre",
      AL: "Alagoas",
      AP: "Amapá",
      AM: "Amazonas",
      BA: "Bahia",
      CE: "Ceará",
      DF: "Distrito Federal",
      ES: "Espírito Santo",
      GO: "Goiás",
      MA: "Maranhão",
      MT: "Mato Grosso",
      MS: "Mato Grosso do Sul",
      MG: "Minas Gerais",
      PA: "Pará",
      PB: "Paraíba",
      PR: "Paraná",
      PE: "Pernambuco",
      PI: "Piauí",
      RJ: "Rio de Janeiro",
      RN: "Rio Grande do Norte",
      RS: "Rio Grande do Sul",
      RO: "Rondônia",
      RR: "Roraima",
      SC: "Santa Catarina",
      SP: "São Paulo",
      SE: "Sergipe",
      TO: "Tocantins",
    }),
    [],
  );

  // Validações do formulário
  const nameInvalid = useMemo(() => {
    return !customerInfo.name?.trim();
  }, [customerInfo.name]);

  const phoneInvalid = useMemo(() => {
    const d = onlyDigits(customerInfo.phone || "");
    return d.length < 10; // Pelo menos DDD + 8 dígitos
  }, [customerInfo.phone, onlyDigits]);

  const cepInvalid = useMemo(() => {
    const d = onlyDigits(address.cep || "");
    return d.length !== 8;
  }, [address.cep, onlyDigits]);

  const logradouroInvalid = useMemo(() => {
    return !address.logradouro?.trim();
  }, [address.logradouro]);

  const bairroInvalid = useMemo(() => {
    return !address.bairro?.trim();
  }, [address.bairro]);

  const ufInvalid = useMemo(() => {
    return !address.uf?.trim();
  }, [address.uf]);

  const numeroInvalid = useMemo(() => {
    return !address.numero?.trim();
  }, [address.numero]);

  const nameCodeEmail = useMemo(() => {
    return !customerInfo.code?.trim() || customerInfo.code.length < 6;
  }, [customerInfo.code]);

  const isValidationCodeEmail = useMemo(() => {
    return !nameCodeEmail;
  }, [nameCodeEmail]);

  const isDelivery = useMemo(() => {
    return deliveryType === "delivery";
  }, [deliveryType]);

  const isFormValid = useMemo(
    () =>
      !nameInvalid &&
      !phoneInvalid &&
      (!isDelivery ||
        (!cepInvalid &&
          !logradouroInvalid &&
          !bairroInvalid &&
          !ufInvalid &&
          !numeroInvalid)),
    [
      nameInvalid,
      phoneInvalid,
      isDelivery,
      cepInvalid,
      logradouroInvalid,
      bairroInvalid,
      ufInvalid,
      numeroInvalid,
    ],
  );

  // UI Busy overlay for any button action
  const withBusy = useMemo(
    () =>
      (fn) =>
      async (...args) => {
        try {
          setUiBusy(true);
          const ret = fn?.(...args);
          if (ret && typeof ret.then === "function") await ret;
        } finally {
          setUiBusy(false);
        }
      },
    [],
  );

  // Função para limpar dados do pedido do localStorage
  const clearOrderFromLocalStorage = useCallback(() => {
    localStorage.removeItem("pedido.codigo");
    localStorage.removeItem("pedido.status");
    localStorage.removeItem("pedido.delivery");
    console.log("✅ Pedido concluído - localStorage limpo");
  }, []);

  const mockWebSocket = useMemo(
    () => ({
      id: codeOrders,
      type: deliveryType === "pickup" ? "Retirada" : "Delivery",
      eta: "23–46 min",
      address: `Endereço: ${addressOrders}`,
      steps: [
        {
          key: "received",
          label: "Aguardando",
          icon: <CheckCircle2 className="w-5 h-5 text-gray-400" />,
        },
        {
          key: "preparing",
          label: "Preparando",
          icon: <CookingPot className="w-5 h-5 text-gray-400" />,
        },
        {
          key: "on_the_way",
          label:
            deliveryType === "pickup"
              ? "Retirada liberada"
              : "Saiu para entrega",
          icon: <Truck className="w-5 h-5 text-gray-400" />,
        },
        {
          key: "delivered",
          label: "Entregue",
          icon: <CheckCircle2 className="w-5 h-5 text-gray-400" />,
        },
      ],
      currentStep: webSocket,
    }),
    [codeOrders, deliveryType, addressOrders, webSocket],
  );

  const orderStatusLine = useMemo(
    () => ({
      id: codeOrders,
      type: deliveryType === "pickup" ? "Retirada" : "Delivery",
      steps: [
        { key: "0", label: "Aguardando" },
        { key: "1", label: "Preparando" },
        { key: "2", label: "Liberado" },
        { key: "3", label: "Entregue" },
        { key: "4", label: "Finalizado" },
        { key: "5", label: "Cancelado" },
      ],
      currentStep: webSocket,
    }),
    [codeOrders, deliveryType, webSocket],
  );

  // Banner data for carousel
  const banners = useMemo(
    () => [
      {
        id: 1,
        title: "Novidade! Geladinhos Especiais",
        subtitle: "Sabores únicos com ingredientes Premium",
        image:
          "https://images.unsplash.com/photo-1505394033641-40c6ad1178d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxwb3BzaWNsZXN8ZW58MHx8fHwxNzU0Nzc1Njk5fDA&ixlib=rb-4.1.0&q=85",
        cta: "Experimente Agora",
      },
      {
        id: 2,
        title: "Açaí Premium com 20% OFF",
        subtitle: "Ingredientes selecionados e sabor incomparável",
        image:
          "https://images.unsplash.com/photo-1684403620650-81dc661a69db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhY2FpfGVufDB8fHx8MTc1NDc3NTcwOXww&ixlib=rb-4.1.0&q=85",
        cta: "Peça Já",
      },
      {
        id: 3,
        title: "Trufas Artesanais",
        subtitle: "Chocolate belga em criações exclusivas",
        image:
          "https://images.unsplash.com/photo-1601170022270-39cac948bfdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHx0cnVmZmxlc3xlbnwwfHx8fDE3NTQ3NzU3MDR8MA&ixlib=rb-4.1.0&q=85",
        cta: "Descubra",
      },
    ],
    [],
  );

  // 🔹 função para scroll suave até a seção de produtos (ajustando o header fixo)
  const scrollToProducts = useCallback(() => {
    if (!productsSectionRef.current) return;
    // desconta a barra fixa de busca/categorias (+ respiro)
    const toolbar = document.getElementById(CATALOG_TOOLBAR_ID);
    const headerOffset = (toolbar?.offsetHeight ?? 0) + 12;
    const elementPosition =
      productsSectionRef.current.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }, []);

  // Definir fetchProducts antes dos handlers que o utilizam
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Só adiciona filtro de categoria se houver uma categoria selecionada
      if (selectedCategory && selectedCategory !== "") {
        params.append("category", selectedCategory);
      }
      // Só adiciona filtro de busca se houver termo
      if (searchTerm && searchTerm.trim() !== "") {
        params.append("search", searchTerm.trim());
      }

      const endpoint = params.toString() ? `/products?${params}` : "/products";
      const data = await api.get(endpoint);

      // Normaliza price para Number (vindo de decimal(10,2) ou string)
      // Filtra produtos da categoria "festa" APENAS quando não há categoria selecionada (modo Todos)
      const products = data.data
        .filter((p) => {
          // Se há uma categoria selecionada, não filtra nada (mostra inclusive festa se selecionada)
          if (selectedCategory && selectedCategory !== "") {
            return true;
          }
          // Se está em "Todos" (sem categoria), filtra festa
          const category = (p.category || "").toLowerCase().trim();
          return category !== "festa" && category !== "festas";
        })
        .map((p) => ({
          ...p,
          price: (() => {
            if (typeof p.price === "number") return p.price;
            const n = Number(
              String(p.price)
                .replace(/[^\d,,.-]/g, "")
                .replace(/\.(?=.*\.)/g, "")
                .replace(",", "."),
            );
            return Number.isFinite(n) ? n : 0;
          })(),
        }));
      setProducts(products);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, [api, selectedCategory, searchTerm]);

  // 🔹 clique na categoria = seleciona + faz scroll
  const handleCategoryClick = useCallback(
    (categoryId) => {
      setSelectedCategory(categoryId);
      scrollToProducts();
    },
    [scrollToProducts],
  );

  // Handler para busca com debounce
  const handleSearchChange = useCallback((searchValue) => {
    setSearchTerm(searchValue);
  }, []);

  // Verifica status do pedido no backend
  const checkOrderStatus = useCallback(
    async (codigo) => {
      if (!codigo || codigo === "0") return null;
      try {
        const data = await api.get(`/orders/${codigo}/status`);
        return parseOrderStatus(data); // Backend retorna o id do status (0-5) como número puro
      } catch (error) {
        console.error("Erro ao verificar status do pedido:", error);
        return null;
      }
    },
    [api],
  );

  //apresentar status pedido line
  const handleOpenModal = useCallback(() => {
    console.log("-----> " + codeOrders + " ---- " + webSocket);
    openOrderStatusModal(codeOrders);
  }, [codeOrders, webSocket, openOrderStatusModal]);

  // Fecha modal de acompanhamento
  const handleCloseStatusModal = useCallback(async () => {
    // Verifica status atual no backend antes de fechar
    const backendStatus = await checkOrderStatus(orderCode || codeOrders);
    if (backendStatus !== null && backendStatus >= 4) {
      // Se finalizado, limpa localStorage
      clearOrderFromLocalStorage();
      setAcompanhamento(false);
      setCodeOrders("0");
      setWebSocket(0);
    }
    closeOrderStatusModal();
  }, [
    orderCode,
    codeOrders,
    checkOrderStatus,
    clearOrderFromLocalStorage,
    closeOrderStatusModal,
  ]);

  // Callback chamado quando um pedido é criado
  const handleOrderCreated = useCallback((codigo, orderAddress) => {
    console.log("🔔 Pedido criado recebido no App.js:", codigo);
    setCodeOrders(String(codigo));
    setWebSocket(0); // Status inicial: Aguardando
    setAcompanhamento(true);

    // Formata endereço se disponível
    if (orderAddress && orderAddress.logradouro) {
      const formattedAddress = `${orderAddress.logradouro}, Nº ${
        orderAddress.numero
      }${
        orderAddress.complemento ? " (" + orderAddress.complemento + ")" : ""
      }`;
      setAddressOrders(formattedAddress);
    }
  }, []);

  // Funções auxiliares para rastreamento de produtos no localStorage
  const updateProductsTracking = useCallback((currentCart) => {
    try {
      const qtyMap = {}; // Quantidade total por product_id
      const detailedTracking = []; // Rastreamento detalhado de cada sub-produto

      currentCart.forEach((cartItem) => {
        const cartItemQty = Number(cartItem.quantity ?? 1);
        const isCombo = cartItem.product_creating === "S";

        // 1. Produtos SIMPLES (não combos)
        if (!isCombo && cartItem.product_id) {
          const productId = cartItem.product_id;
          qtyMap[productId] = (qtyMap[productId] || 0) + cartItemQty;

          // Adiciona ao rastreamento detalhado
          detailedTracking.push({
            product_id: productId,
            product_name: cartItem.product_name,
            quantity: cartItemQty,
            type: "simple",
            cart_item_id: cartItem.id,
            parent_combo: null,
          });
        }

        // 2. Produtos COMBO (creating="S") - rastreia o combo em si
        if (isCombo && cartItem.product_id) {
          const productId = cartItem.product_id;
          qtyMap[productId] = (qtyMap[productId] || 0) + cartItemQty;

          // Adiciona ao rastreamento detalhado
          detailedTracking.push({
            product_id: productId,
            product_name: cartItem.product_name,
            quantity: cartItemQty,
            type: "combo",
            cart_item_id: cartItem.id,
            parent_combo: null,
            selected_ingredients: cartItem.selected_ingredients || [],
            selected_produtos: cartItem.selected_produtos || [],
          });
        }

        // 3. Sub-produtos de COMBOS (multiplicado pela qty do combo)
        if (Array.isArray(cartItem.selected_produtos)) {
          cartItem.selected_produtos.forEach((prod) => {
            const productId = prod.id;
            const prodQty = Number(prod.qty ?? 0);
            if (productId && prodQty > 0) {
              const totalQty = prodQty * cartItemQty;
              qtyMap[productId] = (qtyMap[productId] || 0) + totalQty;

              // Adiciona ao rastreamento detalhado
              detailedTracking.push({
                product_id: productId,
                product_name: prod.name,
                quantity: totalQty,
                quantity_per_combo: prodQty,
                combo_quantity: cartItemQty,
                type: "sub-product",
                cart_item_id: cartItem.id,
                parent_combo: {
                  id: cartItem.product_id,
                  name: cartItem.product_name,
                  selected_ingredients: cartItem.selected_ingredients || [],
                  selected_produtos: cartItem.selected_produtos || [],
                },
              });
            }
          });
        }
      });

      // Salva ambas as estruturas no localStorage
      localStorage.setItem("bonfim_products_tracking", JSON.stringify(qtyMap));
      localStorage.setItem(
        "bonfim_products_tracking_detailed",
        JSON.stringify(detailedTracking),
      );

      console.log("🔍 Rastreamento atualizado (total):", qtyMap);
      console.log("🔍 Rastreamento detalhado:", detailedTracking);
    } catch (error) {
      console.error("Erro ao atualizar rastreamento de produtos:", error);
    }
  }, []);

  const clearProductsTracking = useCallback(() => {
    try {
      localStorage.removeItem("bonfim_products_tracking");
      localStorage.removeItem("bonfim_products_tracking_detailed");
      console.log("✅ Rastreamento de produtos limpo");
    } catch (error) {
      console.error("Erro ao limpar rastreamento:", error);
    }
  }, []);

  // Limpa carrinho e dados do formulário após pedido criado
  const handleClearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem("bonfim_cart");
    clearProductsTracking();
    setCustomerInfo({ name: "", phone: "", email: "" });
    setAddress({
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      uf: "",
      estado: "",
    });
    setPaymentMethod("pix");
    setPaymentMethodSelected("pix");
    console.log("✅ Carrinho e formulário limpos");
  }, [clearProductsTracking]);

  // Título da categoria selecionada
  const selectedCategoryTitle = useMemo(() => {
    if (!selectedCategory) return "Todos os produtos";
    const c = categories.find(
      (cat) => String(cat.id) === String(selectedCategory),
    );
    return c?.name ?? "Produtos";
  }, [selectedCategory, categories]);

  // Determina se deve mostrar produtos agrupados por categoria
  const showGroupedProducts = useMemo(() => {
    // Agrupa apenas quando:
    // 1. Nenhuma categoria específica selecionada (mostrando "Todos")
    // 2. Não há termo de busca ativo
    return !selectedCategory && !searchTerm.trim();
  }, [selectedCategory, searchTerm]);

  // Funções das APIs
  const fetchCategories = useCallback(async () => {
    try {
      const data = await api.get("/categories");
      setCategories(data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [api]);

  const fetchProductExtras = useCallback(async () => {
    try {
      const data = await api.get("/product-extras");
      setProductsExtras(data.data);
    } catch (error) {
      console.error("Error fetching product-extras:", error);
    }
  }, [api]);

  const fetchPayments = useCallback(async () => {
    try {
      const data = await api.get("/payments");
      setPayments(data.data);
    } catch (error) {
      console.error("Erro para carregar os meio de pagamentos:", error);
    }
  }, [api]);

  const fetchDeliveryValidate = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/companies/${COMPANY_KEY}/delivery`);

      //deliveryEnabled
      if (
        data === false ||
        data?.data === false ||
        data?.data?.[0]?.delivery === false
      ) {
        setDeliveryEnabled(false);
        setDeliveryType("pickup");
        return {
          success: false,
          message: "Somente Retirada, obrigado.",
        };
      } else {
        setDeliveryEnabled(true);
        setDeliveryType("delivery");
        return {
          success: true,
          message: "Delivery disponível! Você pode prosseguir com o pedido.",
        };
      }
    } catch (e) {
      console.error(e);
      return {
        success: false,
        message: "Não foi possível validar o delivery. Tente novamente.",
      };
    } finally {
      setLoading(false);
    }
  }, [api]);

  const createPixCharge = useCallback(async () => {
    try {
      setPixLoading(true);
      const amount = getTotalPrice();
      const data = await api.post("/payments/mercadopago/pix", {
        amount,
        description: company?.name ? `Pedido ${company.name}` : "Pedido",
        customer: {
          name: customerInfo.name,
          phone: onlyDigits(customerInfo.phone),
        },
        items: cart.map((i) => ({
          id: String(i.product_id),
          title: i.product_name,
          quantity: i.quantity,
          unit_price: i.product_price,
        })),
      });
      const td =
        (data.point_of_interaction &&
          data.point_of_interaction.transaction_data) ||
        {};
      setPixData({
        id: data.id || data.payment_id || "",
        qr_code: data.qr_code || td.qr_code || "",
        qr_code_base64: data.qr_code_base64 || td.qr_code_base64 || "",
        copy_paste: data.copy_paste || td.qr_code || "",
      });
    } catch (e) {
      console.error(e);
      alert("Não foi possível gerar o PIX agora.");
    } finally {
      setPixLoading(false);
    }
  }, [api, company]);

  const addToCart = useCallback(
    (
      product,
      quantity = 1,
      comments = "",
      selectedIngredients = [],
      selectedProdutos = [],
    ) => {
      // Para produtos combo (creating="S"), cria identificador único baseado nas seleções
      const isCombo = product.creating === "S";

      // Cria chave única para identificar combinação de produto + seleções
      const getItemKey = (item) => {
        const ingredientsKey =
          item.selected_ingredients
            ?.map((ing) => ing.id || ing)
            .sort()
            .join(",") || "";
        const produtosKey =
          item.selected_produtos
            ?.map((prod) => prod.id || prod)
            .sort()
            .join(",") || "";
        return `${item.product_id}|${ingredientsKey}|${produtosKey}`;
      };

      const currentItemKey = getItemKey({
        product_id: product.id,
        selected_ingredients: selectedIngredients,
        selected_produtos: selectedProdutos,
      });

      // Para combos, verifica se existe exatamente a mesma combinação
      // Para produtos simples, verifica apenas pelo product_id
      const existingItem = isCombo
        ? cart.find((item) => getItemKey(item) === currentItemKey)
        : cart.find((item) => item.product_id === product.id);

      // Validação de estoque disponível
      if (product.quantity !== undefined) {
        const currentQtyInCart = existingItem ? existingItem.quantity : 0;
        const newTotalQty = currentQtyInCart + quantity;

        if (newTotalQty > product.quantity) {
          const availableToAdd = product.quantity - currentQtyInCart;
          if (availableToAdd <= 0) {
            setStockWarning({
              show: true,
              title: "Estoque Esgotado",
              message: `Você já tem a quantidade máxima deste produto no carrinho (${product.quantity} disponíveis).`,
            });
            return;
          } else {
            setStockWarning({
              show: true,
              title: "Estoque Limitado",
              message: `Apenas ${availableToAdd} unidade(s) disponível(is) para adicionar. Estoque: ${product.quantity}.`,
            });
            quantity = availableToAdd;
          }
        }
      }

      if (existingItem) {
        // Atualiza quantidade do item existente com a mesma combinação
        const updatedCart = cart.map((item) =>
          getItemKey(item) === currentItemKey
            ? {
                ...item,
                quantity: item.quantity + quantity,
                comments,
              }
            : item,
        );
        setCart(updatedCart);
        updateProductsTracking(updatedCart);
      } else {
        // Cria novo item no carrinho
        const newItem = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          product_id: product.id,
          product_name: product.name,
          product_price: product.price,
          product_image: product.image_url,
          product_quantity_available: product.quantity,
          product_creating: product.creating, // Armazena se é combo
          quantity,
          comments,
          selected_ingredients: selectedIngredients,
          selected_produtos: selectedProdutos,
        };
        const updatedCart = [...cart, newItem];
        setCart(updatedCart);
        updateProductsTracking(updatedCart);
      }
    },
    [cart, updateProductsTracking],
  );

  const removeFromCart = useCallback(
    (itemId) => {
      const updatedCart = cart.filter((item) => item.id !== itemId);
      setCart(updatedCart);
      updateProductsTracking(updatedCart);
    },
    [cart, updateProductsTracking],
  );

  const updateCartQuantity = useCallback(
    (itemId, newQuantity) => {
      console.log("Click carrinho: " + itemId + " : " + newQuantity);
      if (newQuantity <= 0) {
        removeFromCart(itemId);
      } else {
        setCart((prevCart) => {
          const updatedCart = prevCart.map((item) => {
            if (item.id === itemId) {
              // Tenta usar estoque armazenado no item ou busca nos produtos
              let maxAllowed = item.product_quantity_available;

              if (maxAllowed === undefined) {
                const productInStock = products.find(
                  (p) => p.id === item.product_id,
                );
                maxAllowed = productInStock?.quantity;
              }

              if (maxAllowed !== undefined) {
                // Calcula quanto já está no carrinho deste produto (excluindo o item atual)
                const otherItemsQty = prevCart
                  .filter(
                    (cartItem) =>
                      cartItem.product_id === item.product_id &&
                      cartItem.id !== itemId,
                  )
                  .reduce((sum, cartItem) => sum + cartItem.quantity, 0);

                // Estoque disponível = total - outras quantidades
                const availableForThisItem = Math.max(
                  0,
                  maxAllowed - otherItemsQty,
                );
                const finalQuantity = Math.min(
                  newQuantity,
                  availableForThisItem,
                );

                if (newQuantity > availableForThisItem) {
                  setStockWarning({
                    show: true,
                    title: "Estoque Limitado",
                    message: `Quantidade máxima disponível: ${availableForThisItem} unidade(s). Estoque total: ${maxAllowed}.`,
                  });
                }

                return { ...item, quantity: finalQuantity };
              }

              return { ...item, quantity: newQuantity };
            }
            return item;
          });

          // Atualiza rastreamento após modificar o carrinho
          updateProductsTracking(updatedCart);
          return updatedCart;
        });
      }
    },
    [products, removeFromCart, updateProductsTracking],
  );

  const getTotalPrice = useCallback(() => {
    return cart.reduce(
      (total, item) => total + item.product_price * item.quantity,
      0,
    );
  }, [cart]);

  const getTotalItems = useCallback(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const openProductModal = useCallback((product) => {
    setSelectedProduct(product);
    setIsProductModalOpen(true);
  }, []);

  const validarDelivery = useCallback(async () => {
    const result = await fetchDeliveryValidate();
    return result;
  }, [fetchDeliveryValidate]);

  const sendValidationCode = useCallback(() => {
    alert("Validando...");
  }, []);

  const sendToWhatsApp = useCallback(async () => {
    try {
      const name = String(customerInfo.name || "").trim();
      const phoneDigits = String(customerInfo.phone || "").replace(/\D/g, "");

      await sendOrderToWhatsApp(
        name,
        phoneDigits,
        deliveryType,
        address,
        paymentMethod,
        paymentMethodSelected,
        cart,
        getTotalPrice,
        (codigo) => {
          // Callback de sucesso
          setCodeOrders(codigo);
          const addressOrders = `${address.logradouro}, Nº ${address.numero}${
            address.complemento ? " (" + address.complemento + ")" : ""
          }`;
          setAddressOrders(addressOrders);

          // Limpar carrinho, localStorage e fechar modal
          setCart([]);
          localStorage.removeItem("bonfim_cart");
          setIsCartOpen(false);
          setCustomerInfo({ name: "", phone: "" });
          setAddress({
            cep: "",
            logradouro: "",
            numero: "",
            complemento: "",
            bairro: "",
            uf: "",
            estado: "",
          });
          setPaymentMethod("PIX");
          setPixPayNow(false);
          setPixData({
            id: "",
            qr_code: "",
            qr_code_base64: "",
            copy_paste: "",
          });
          setOpen(true);
        },
      );
    } catch (error) {
      alert(error.message || "Não foi possível enviar o pedido para WhatsApp.");
    }
  }, [sendOrderToWhatsApp]);

  // useEffect hooks
  useEffect(() => {
    const initData = async () => {
      if (!initialLoading) return; // Evita execução múltipla

      // Aguarda autenticação estar completa
      if (authLoading) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      try {
        console.log("🚀 Inicializando dados da aplicação...");
        // Executa as chamadas de API sequencialmente para evitar sobrecarga
        await fetchCategories();
        await fetchProducts();
        await fetchPayments();

        const rawDelivery = localStorage.getItem("pedido.delivery");
        if (rawDelivery != null && Number(rawDelivery) === 0) {
          setAcompanhamento(true);
        } else {
          setAcompanhamento(false);
        }

        console.log("✅ Dados iniciais carregados com sucesso");
      } catch (error) {
        console.error("❌ Erro ao carregar dados iniciais:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    initData();
  }, [initialLoading, authLoading, isAuthenticated]);

  // Salva carrinho no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem("bonfim_cart", JSON.stringify(cart));
    } catch (error) {
      console.error("Erro ao salvar carrinho no localStorage:", error);
    }
  }, [cart]);

  // Sincroniza carrinho com localStorage quando navega de volta para home
  useEffect(() => {
    // Só sincroniza quando está na rota raiz (home)
    if (location.pathname === "/") {
      try {
        const savedCart = localStorage.getItem("bonfim_cart");
        if (!savedCart || savedCart === "[]") {
          // Se não há carrinho no localStorage ou está vazio, limpa o estado
          if (cart.length > 0) {
            console.log("🧹 Sincronizando: Limpando carrinho no estado");
            setCart([]);
          }
        } else {
          // Se há carrinho no localStorage, sincroniza
          const parsedCart = JSON.parse(savedCart);
          if (JSON.stringify(parsedCart) !== JSON.stringify(cart)) {
            console.log("🔄 Sincronizando carrinho do localStorage");
            setCart(parsedCart);
          }
        }

        // Recarrega dados quando volta para home (ex: saindo do checkout)
        if (!initialLoading && isAuthenticated) {
          console.log("🔄 Recarregando dados ao voltar para home...");
          fetchCategories();
          fetchProducts();
          fetchPayments();
          fetchProductExtras();
        }
      } catch (error) {
        console.error("Erro ao sincronizar carrinho do localStorage:", error);
      }
    }
  }, [
    location.pathname,
    initialLoading,
    isAuthenticated,
    fetchCategories,
    fetchProducts,
    fetchPayments,
    fetchProductExtras,
  ]); // Executa quando a rota muda

  // Reage a mudanças de categoria para buscar produtos
  useEffect(() => {
    if (!initialLoading && isAuthenticated) {
      fetchProducts();
    }
  }, [selectedCategory]);

  // Reage a mudanças de busca com debounce
  useEffect(() => {
    if (!initialLoading && isAuthenticated) {
      // Cancela timeout anterior se existir
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce para evitar muitas chamadas ao digitar
      searchTimeoutRef.current = setTimeout(() => {
        fetchProducts();
      }, 500);

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [searchTerm]);

  // Busca CEP automaticamente (ViaCEP)
  useEffect(() => {
    const d = onlyDigits(address.cep);
    if (d.length === 8) {
      fetch(`https://viacep.com.br/ws/${d}/json/`)
        .then((r) => r.json())
        .then((data) => {
          if (data.erro) {
            alert("CEP não encontrado. Verifique e tente novamente.");
            setAddress((prev) => ({
              ...prev,
              logradouro: "",
              bairro: "",
              uf: "",
              estado: "",
            }));
            return;
          }
          const uf = data.uf || "";
          setAddress((prev) => ({
            ...prev,
            logradouro: data.logradouro || "",
            bairro: data.bairro || "",
            localidade: data.localidade || "",
            uf,
            estado: UF_NAMES[uf] || "",
          }));
        })
        .catch(() => alert("Não foi possível consultar o CEP agora."));
    }
  }, [address.cep, onlyDigits, UF_NAMES]);

  // Bloqueia o scroll do body quando qualquer modal estiver aberto
  useEffect(() => {
    const anyOpen = isProductModalOpen || isCartOpen;
    if (anyOpen) {
      const prevOverflow = document.body.style.overflow;
      const prevTouchAction = document.body.style.touchAction;
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
      return () => {
        document.body.style.overflow = prevOverflow || "";
        document.body.style.touchAction = prevTouchAction || "";
      };
    }
  }, [isProductModalOpen, isCartOpen]);

  // Formata telefone inicial uma única vez
  useEffect(() => {
    setCustomerInfo((prev) => ({ ...prev, phone: maskPhoneBR(prev.phone) }));
  }, []);

  // Carrega estado inicial do localStorage apenas uma vez
  useEffect(() => {
    const rawStatus = Number(localStorage.getItem("pedido.status") ?? 0);
    const rawCodigo = localStorage.getItem("pedido.codigo") ?? "0";
    const rawDelivery = Number(localStorage.getItem("pedido.delivery") ?? 1);

    // 🔹 Se o pedido está ativo (status < 4), mostrar acompanhamento
    if (Number(rawCodigo) > 0 && rawStatus < 4) {
      setWebSocket(rawStatus);
      setCodeOrders(rawCodigo);
      setAcompanhamento(true);
    } else if (rawStatus >= 4) {
      // Se já está finalizado, limpa imediatamente
      clearOrderFromLocalStorage();
      setAcompanhamento(false);
      setCodeOrders("0");
      setWebSocket(0);
    }
  }, [clearOrderFromLocalStorage]); // Executa apenas na montagem do componente

  // Polling para atualizar status do pedido a cada 10 segundos
  useEffect(() => {
    if (!codeOrders || codeOrders === "0" || !acompanhamento) return;

    const interval = setInterval(async () => {
      const backendStatus = await checkOrderStatus(codeOrders);
      if (backendStatus !== null) {
        // Atualiza localStorage com status do backend
        localStorage.setItem("pedido.status", String(backendStatus));
        setWebSocket(backendStatus);

        // Se o backend indicar que foi finalizado (>= 4), limpar
        if (backendStatus >= 4) {
          clearOrderFromLocalStorage();
          setAcompanhamento(false);
          setOpen(false);
          setCodeOrders("0");
          setWebSocket(0);
          clearInterval(interval);
        }
      }
    }, 10000); // Verifica a cada 10 segundos

    return () => clearInterval(interval);
  }, [
    codeOrders,
    acompanhamento,
    checkOrderStatus,
    clearOrderFromLocalStorage,
  ]);

  // Early return para tela de carregamento
  if (authLoading) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    // Ex.: empresa inexistente/fechada (TENANT004) → "Loja indisponível no momento."
    return (
      <AuthLoadingScreen {...(authError && { message: authError.message })} />
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="min-h-screen bg-cream text-cocoa">
            {/* Header */}
            <Header
              company={company}
              totalItems={getTotalItems()}
              totalPrice={getTotalPrice()}
              formatBRL={formatBRL}
              onCartClick={() => setIsCartOpen(true)}
            />

            {/* Busca + categorias (sticky) */}
            <SearchAndFilters
              searchTerm={searchTerm}
              setSearchTerm={handleSearchChange}
              selectedCategory={selectedCategory}
              setSelectedCategory={handleCategoryClick}
              categories={categories}
            />

            {acompanhamento && (
              <OrderStatusLine
                order={orderStatusLine}
                onOpenModal={() => handleOpenModal()}
              />
            )}

            {uiBusy && (
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]">
                <img
                  src="https://media.giphy.com/media/y1ZBcOGOOtlpC/giphy.gif"
                  alt="Carregando..."
                  className="w-24 h-24 rounded-xl shadow-lg"
                />
              </div>
            )}

            {/* Banner Carousel - Controlado por feature flag */}
            {FEATURES.SHOW_BANNER_CAROUSEL && (
              <BannerCarousel banners={banners} onCtaClick={scrollToProducts} />
            )}

            {/* Acompanhamento Pedido Modal */}
            {isOrderStatusModalOpen && (
              <OrderStatusModal
                isOpen={isOrderStatusModalOpen}
                onClose={handleCloseStatusModal}
                order={mockWebSocket}
                pedidoId={orderCode || codeOrders}
              />
            )}

            {/* Cart Modal – fluxo em etapas */}
            <CartStepsModal
              isOpen={isCartOpen}
              onClose={() => setIsCartOpen(false)}
              cart={cart}
              updateCartQuantity={updateCartQuantity}
              removeFromCart={removeFromCart}
              getTotalPrice={getTotalPrice}
              deliveryType={deliveryType}
              setDeliveryType={setDeliveryType}
              validarDelivery={validarDelivery}
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              address={address}
              setAddress={setAddress}
              maskCEP={maskCEP}
              onlyDigits={onlyDigits}
              nameInvalid={nameInvalid}
              phoneInvalid={phoneInvalid}
              cepInvalid={cepInvalid}
              logradouroInvalid={logradouroInvalid}
              bairroInvalid={bairroInvalid}
              ufInvalid={ufInvalid}
              numeroInvalid={numeroInvalid}
              isFormValid={isFormValid}
              payments={payments}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentMethodSelected={paymentMethodSelected}
              setPaymentMethodSelected={setPaymentMethodSelected}
              withBusy={withBusy}
              sendToWhatsApp={sendToWhatsApp}
              handleOpenStatusModal={handleOpenModal}
              onOrderCreated={handleOrderCreated}
              onClearCart={handleClearCart}
            />

            {/* Products Grid */}
            <ProductsGrid
              ref={productsSectionRef}
              products={products}
              categories={categories}
              loading={loading}
              selectedCategory={selectedCategory}
              selectedCategoryTitle={selectedCategoryTitle}
              showGrouped={showGroupedProducts}
              openProductModal={openProductModal}
              formatBRL={formatBRL}
            />

            {/* Scroll to Top Button */}
            <ScrollToTopButton />

            {/* WhatsApp Contact Button */}
            {/* WhatsApp Contact Button (só com o telefone real da loja) */}
            {getWhatsAppNumber(company) && (
              <WhatsAppButton
                phoneNumber={getWhatsAppNumber(company)}
                message={`Olá! Gostaria de fazer um pedido pelo ${company.name}. Poderia me ajudar?`}
              />
            )}

            {/* Product Modal */}
            <ProductModal
              isOpen={isProductModalOpen}
              onClose={() => setIsProductModalOpen(false)}
              product={selectedProduct}
              creating={selectedProduct?.creating ?? "N"}
              hasExtraIngredients={selectedProduct?.hasExtraIngredients ?? "N"}
              hasExtraProducts={selectedProduct?.hasExtraProducts ?? "N"}
              addToCart={addToCart}
              sizes={modalSizes}
              onOpenCart={() => setIsCartOpen(true)}
              cart={cart}
            />

            {/* Modal para validar PIN por e-mail */}
            {isCartValidateCode && (
              <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-100">
                <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-800">
                      Validar código de autenticação
                    </h2>
                    <button
                      onClick={() => setIsCartValidateCode(false)}
                      className="bg-gray-100 hover:bg-gray-200 rounded-full p-2"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="overflow-y-auto max-h-[50vh] pr-1">
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        <input
                          type="text"
                          placeholder="000000"
                          inputMode="numeric"
                          maxLength="6"
                          value={customerInfo.code}
                          onChange={(e) => {
                            const onlyD = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6);
                            setCustomerInfo({ ...customerInfo, code: onlyD });
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = (
                              e.clipboardData.getData("text") || ""
                            )
                              .replace(/\D/g, "")
                              .slice(0, 6);
                            setCustomerInfo({ ...customerInfo, code: pasted });
                          }}
                          className="text-center tracking-[0.25em] text-5xl md:text-6xl font-semibold font-mono tabular-nums leading-none px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                          style={{
                            borderColor: nameCodeEmail
                              ? "rgb(var(--color-brand-600))"
                              : undefined,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={withBusy(sendValidationCode)}
                    disabled={!isValidationCodeEmail}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-xl font-bold text-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Validar código
                  </button>
                </div>
              </div>
            )}

            {/* Modal de Aviso de Estoque */}
            {stockWarning.show && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[10000]">
                <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-fade-in">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {stockWarning.title}
                    </h3>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {stockWarning.message}
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      setStockWarning({ show: false, title: "", message: "" })
                    }
                    className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3 px-6 rounded-xl font-semibold transition-colors"
                  >
                    Entendi
                  </button>
                </div>
              </div>
            )}

            {/* Footer */}
            <Footer company={company} />

            {/* Barra fixa da sacola (celular) */}
            <MobileCartBar
              totalItems={getTotalItems()}
              totalPrice={getTotalPrice()}
              formatBRL={formatBRL}
              onOpen={() => setIsCartOpen(true)}
            />
          </div>
        }
      />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/pagamento/retorno" element={<PaymentReturnPage />} />
    </Routes>
  );
}

// Componente wrapper que fornece o contexto de OrderStatus
function AppWithOrderStatusProvider() {
  return (
    <OrderStatusProvider>
      <App />
    </OrderStatusProvider>
  );
}

export default AppWithOrderStatusProvider;
